/**
 * A failed database request that keeps the database's error CODE.
 *
 * WHY. `throw new Error(error.message)` — the pattern across this package —
 * keeps the words and drops the code. A caller that must decide whether to
 * RETRY cannot tell "the network dropped" from "the server refused this" by
 * reading an English sentence, and the Cataloging app's capture queue has to
 * make exactly that call: retry a warehouse wifi blip, but stop re-sending a
 * write the database will never accept.
 */
export class DataRequestError extends Error {
  readonly code: string | null;

  constructor(error: { message: string; code?: string | null }) {
    super(error.message);
    this.name = 'DataRequestError';
    this.code = error.code && error.code.trim() !== '' ? error.code : null;
  }
}

/**
 * Codes that mean "try again later" even though the server answered:
 *  - PGRST301 / PGRST303 — the session token expired or could not be read.
 *    The client refreshes it, so the next attempt normally succeeds.
 *  - 57014 — statement timeout; 40001 / 40P01 — serialisation failure and
 *    deadlock; 53xxx — the server is short of resources. All transient.
 */
const TRANSIENT_CODES = /^(PGRST301|PGRST303|57014|40001|40P01|53\d{3})$/;

/**
 * Should this failure stop being retried?
 *
 * No code at all means the request never got an answer — the network — so it
 * is worth retrying. A code the server chose is a refusal, unless it is one of
 * the transient ones above.
 */
export function isPermanentRefusal(err: unknown): boolean {
  if (!(err instanceof DataRequestError)) return false;
  if (err.code === null) return false;
  return !TRANSIENT_CODES.test(err.code);
}

/** The transient codes that mean the session, not the server: a sign-in fixes them. */
const SESSION_CODES = /^(PGRST301|PGRST303)$/;

/**
 * What went wrong, in words a person can act on — one step finer than
 * `isPermanentRefusal`, and the same split underneath:
 *
 *   no_response  no code: the request never got an answer
 *   signed_out   the session expired or could not be read
 *   server       the server answered, but busy or broken — try again
 *   refused      the server said no, and will say it again
 *
 * The Cataloging app's sync line uses it (Jim, 2026-10-06: tell the worker
 * what is actually wrong, not "No connection" for everything). Anything that
 * is not a `DataRequestError` is read as no answer, as `isPermanentRefusal`
 * reads it.
 */
export type RequestFailure = 'no_response' | 'signed_out' | 'server' | 'refused';

export function requestFailure(err: unknown): RequestFailure {
  if (!(err instanceof DataRequestError) || err.code === null) return 'no_response';
  if (SESSION_CODES.test(err.code)) return 'signed_out';
  if (TRANSIENT_CODES.test(err.code)) return 'server';
  return 'refused';
}
